import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Button, Menu, MenuItem, Tooltip, Box, Divider } from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import { fCurrency } from '../../../../utils/formatNumber';
import { calculatePpd } from '../../../../utils/calculatePpd';
import Iconify from '../../../../reusable-components/iconify';
import { useDispatch, useSelector } from '../../../../redux/store';
import { updateUserSettings } from '../../../../redux/slices/user';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import OrderTotalBar from './OrderTotalBar';

// ----------------------------------------------------------------------
OrderTotal.propTypes = {
  activeRows: PropTypes.array,
  handleVendorFilterClick: PropTypes.func,
  selectedVendor: PropTypes.object,
};

export default function OrderTotal({ activeRows, handleVendorFilterClick, selectedVendor }) {
  const { id = '' } = useParams();
  const [vendorTotals, setVendorTotals] = useState([]);
  const { orderById, tempOrderListById = [] } = useSelector((state) => state.orders);
  const { vendorMinimums, items = [] } = orderById[id] || {};
  const allItems = useMemo(() => [...(items || []), ...(tempOrderListById[id] || [])], [items, tempOrderListById, id]);
  const {
    data: { budgetPpdInfo },
  } = useSelector((state) => state.orderSheet);
  const { user: { settings } } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isDisplayingPPDValues, setIsDisplayingPPDValues] = useState(false);
  const [isSavingPpdSetting, setIsSavingPpdSetting] = useState(false);

  useEffect(() => {
    setIsDisplayingPPDValues(settings?.find((setting) => setting?.areaId === `DisplayBudgetPPD`)?.show || false);
  }, [settings]);

  const saveIsDisplayingPPDVales = () => {
    setIsSavingPpdSetting(true);
    dispatch(updateUserSettings([{
      areaId: `DisplayBudgetPPD`,
      field: '',
      show: !isDisplayingPPDValues,
      style: '',
      value: null,
    }])).then(() => setIsSavingPpdSetting(false))
    .catch(() => {
      setIsSavingPpdSetting(false);
      enqueueSnackbar('Error saving PPD display setting', { variant: 'error' });
    });
    setIsDisplayingPPDValues(!isDisplayingPPDValues);
  };

  const getPpd = useCallback(
    (items) =>
      items.reduce((acc, item) => {
        const { censusAmount, amountDaysInBudget } = budgetPpdInfo?.[item.agoraCategory?.id] || {};
        const ppd = calculatePpd(item.price * item.quantity, censusAmount, amountDaysInBudget);
        return ppd === null ? acc : (acc ?? 0) + ppd;
      }, null),
    [budgetPpdInfo]
  );

  useEffect(() => {
    const getVendorsTotal = (items) =>
      items
        .filter((item) => item.approved) // filter only approved items
        .reduce((acc, item) => {
          const { vendorId, price, quantity, vendor } = item;
          const vendorMinimum = vendorMinimums?.find((min) => min.vendorId === vendorId);
          const total = (price ?? 0) * (quantity ?? 0);
          const vendorTotal = acc.find((total) => total.vendorId === vendorId);
          const ppd = getPpd([item]);
          if (vendorTotal) {
            vendorTotal.total += total;
            if (ppd !== null) vendorTotal.ppd = (vendorTotal.ppd ?? 0) + ppd;
          } else {
            acc.push({
              vendorId,
              vendor,
              total,
              ppd,
              minimum: vendorMinimum?.minimum ?? 0,
              note: vendorMinimum?.note ?? '',
            });
          }
          return acc;
        }, []);

    if (items?.length > 0 || vendorMinimums?.length > 0) {
      setVendorTotals(getVendorsTotal(allItems).sort((a, b) => (a.vendor ?? '').localeCompare(b.vendor ?? '')));
    }
  }, [allItems, vendorMinimums, id, activeRows, items, getPpd]);

  const hasSomeRowsFilteredOut = useMemo(
    () => activeRows?.length && activeRows.length < allItems.length,
    [allItems, activeRows]
  );

  const allFilteredRowsHaveSameSubcategory = useMemo(() => {
    if (!hasSomeRowsFilteredOut) return false;
    const allRowsHaveSameSubcategory = activeRows.every(
      (row) => row.agoraSubcategory.id === activeRows[0].agoraSubcategory.id
    );
    const allRowsWithSubcategoryDisplayed =
      activeRows.length ===
      allItems.filter((item) => item.agoraSubcategory.id === activeRows[0].agoraSubcategory.id).length;
    return allRowsHaveSameSubcategory && allRowsWithSubcategoryDisplayed;
  }, [hasSomeRowsFilteredOut, activeRows, allItems]);

  const allFilteredRowsHaveSameVendor = useMemo(() => {
    if (!hasSomeRowsFilteredOut) return false;
    const allRowsHaveSameVendor = activeRows.every((row) => row.vendorId === activeRows[0].vendorId);
    const allRowsWithVendorDisplayed =
      activeRows.length === allItems.filter((item) => item.vendorId === activeRows[0].vendorId).length;
    return allRowsHaveSameVendor && allRowsWithVendorDisplayed;
  }, [hasSomeRowsFilteredOut, activeRows, allItems]);

  const getTotal = (items) =>
    fCurrency(
      items
        ?.filter((item) => item.approved && item.deleted !== true)
        .reduce((acc, item) => acc + (item.price ?? 0) * (item.quantity ?? 0), 0)
    );

  const orderTotal = useMemo(() => getTotal(allItems), [allItems]);

  const orderPpd = useMemo(
    () => getPpd(allItems.filter((item) => item.approved && item.deleted !== true)),
    [allItems, getPpd]
  );

  const isPpdAvailable = useMemo(
    () => allItems.some((item) => getPpd([item]) !== null),
    [allItems, getPpd]
  );

  const ppdCategoryPeriods = useMemo(
    () =>
      allItems
        .filter((item) => item.approved && item.deleted !== true && getPpd([item]) !== null)
        .reduce((acc, item) => {
          const { id: catId, name } = item.agoraCategory || {};
          const periodLabel = budgetPpdInfo?.[catId]?.periodLabel;
          if (periodLabel && !acc.some((cat) => cat.catId === catId)) acc.push({ catId, name, periodLabel });
          return acc;
        }, []),
    [allItems, budgetPpdInfo, getPpd]
  );

  const hasSinglePpdPeriod = useMemo(
    () => new Set(ppdCategoryPeriods.map((cat) => cat.periodLabel)).size === 1,
    [ppdCategoryPeriods]
  );

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleMenuItemClick = (_, vt) => {
    handleVendorFilterClick({ id: vt.vendorId, name: vt.vendor });
    handleClose();
  };

  const anyVendorHasNote = vendorTotals.some((vt) => vt.note);

  return (
    <>
      <Button
        color="secondary"
        size="small"
        variant={hasSomeRowsFilteredOut ? 'contained' : 'outlined'}
        endIcon={<Iconify icon="mdi:chevron-down" />}
        startIcon={
          vendorTotals.filter((vt) => vt.total < vt.minimum).length > 0 ? (
            hasSomeRowsFilteredOut ? (
              <>
                <Iconify icon="jam:alert" />
                {anyVendorHasNote && <NotesIcon fontSize="small" color="action" />}
              </>
            ) : (
              <>
                <Iconify icon="jam:alert" color="error" />
                {anyVendorHasNote && <NotesIcon fontSize="small" color="action" />}
              </>
            )
          ) : (
            ''
          )
        }
        onClick={handleClick}
      >
        {hasSomeRowsFilteredOut ? (
          <Tooltip title={`Order Total: ${orderTotal}`} arrow placement="top">
            <span>
              {`${
                allFilteredRowsHaveSameSubcategory
                  ? activeRows[0].agoraSubcategory.name
                  : allFilteredRowsHaveSameVendor
                    ? activeRows[0].vendor
                    : ''
              } Total ${getTotal(activeRows)}`}
            </span>
          </Tooltip>
        ) : (
          <Box>Order total {orderTotal}</Box>
        )}
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <Box sx={{ px: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
            <Typography variant="caption" color="secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
              Entire Order
            </Typography>
            {isPpdAvailable && (
              <Tooltip title={isDisplayingPPDValues ? 'Hide PPD Budget' : 'Show PPD Budget'} arrow>
                <Button
                  color="info"
                  size="small"
                  sx={{ minWidth: 0, p: 0.25, my: -0.5, backgroundColor: isDisplayingPPDValues ? 'info.active' : 'transparent' }}
                  onClick={saveIsDisplayingPPDVales}
                  disabled={isSavingPpdSetting}
                >
                  <Iconify icon="mdi:chart-box-outline" color="info" height={18} width={18} />
                </Button>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Order Total</Typography>
            <Typography variant="subtitle2">{orderTotal}</Typography>
          </Box>
          {isDisplayingPPDValues && orderPpd !== null && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="body2">PPD (This Order)</Typography>
              <Typography variant="subtitle2" color="info.main">{`${fCurrency(orderPpd)} per person`}</Typography>
            </Box>
          )}
          {isDisplayingPPDValues && orderPpd !== null && hasSinglePpdPeriod && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right' }}>
              {`Budget period: ${ppdCategoryPeriods[0].periodLabel}`}
            </Typography>
          )}
          {isDisplayingPPDValues &&
            orderPpd !== null &&
            !hasSinglePpdPeriod &&
            ppdCategoryPeriods.map(({ catId, name, periodLabel }) => (
              <Typography key={catId} variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right' }}>
                {`${name} budget period: ${periodLabel}`}
              </Typography>
            ))}
        </Box>
        <Divider />
        <Typography
          variant="caption"
          color="secondary"
          sx={{ display: 'block', fontWeight: 'bold', textTransform: 'uppercase', px: 2, pt: 1 }}
        >
          Vendors
        </Typography>
        {vendorTotals.length > 0 ? (
          <Box>
            {vendorTotals.map((vt, index) => (
              <MenuItem
                onClick={(e) => handleMenuItemClick(e, vt)}
                key={index}
                selected={selectedVendor && selectedVendor.id === vt.vendorId}
              >
                <OrderTotalBar
                  vendorTotal={vt}
                  onClick={(e) => handleMenuItemClick(e, vt)}
                  showPpd={isDisplayingPPDValues}
                />
              </MenuItem>
            ))}

            <Typography
              variant="body2"
              color={selectedVendor ? 'secondary' : 'grey'}
              sx={{
                cursor: selectedVendor ? 'pointer' : '',
                textDecoration: 'underline',
                marginLeft: '16px',
                opacity: selectedVendor ? 1 : 0.6,
              }}
              onClick={() => handleVendorFilterClick(undefined)}
            >
              View all vendors
            </Typography>
          </Box>
        ) : (
          <MenuItem onClick={handleClose}>
            <Typography variant="subtitle2" sx={{ textAlign: 'left' }}>
              There are no vendor minimums
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
